import React, {FunctionComponent, useEffect, useState} from 'react';
import _ from 'lodash';
import {Modal} from '@material-ui/core';
import {ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {ModalType, SnackbarType} from '@treez-ui/core/feedback/modals';
import {useDispatch, useSelector} from 'react-redux';

import PriceTierReassign from './PriceTierReassign';
import {
    ReassignPriceTierPayload,
    getProductsAssignedToPriceTier,
    reassignPriceTiers,
    getPriceTierPresenceInCatalog,
    PriceTierCatalogPresence,
} from '~/utils/ProductAPI';
import {
    selectOrganizationCatalogId,
    selectOrganizationPriceTiersForSelection,
    selectStoresInOrganization,
} from '~/redux/selectors/organizationSelectors';
import {PriceTierWithCatalogPresence} from '~/lib/sharedInterfaces';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';
import {
    openFeedbackModal,
    closeFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {StoreAttributes} from '~/lib/directory';
import {onNotBackdropClickWrap} from '~/lib/backdropClickWrap';

const MAX_NUMBER_OF_PRODUCTS_DISPLAY = 15;

interface PriceTierReassignModalProps {
    onClose: () => void;
    onSubmit: () => void;
    open: boolean;
    priceTier?: PriceTierWithCatalogPresence;
}

const PriceTierReassignModal: FunctionComponent<PriceTierReassignModalProps> = (
    props: PriceTierReassignModalProps
) => {
    const {onClose, onSubmit, open, priceTier} = props;

    const organizationCatalogId: number = useSelector(selectOrganizationCatalogId);
    const stores = useSelector(selectStoresInOrganization);

    const priceTierOptions = useSelector(selectOrganizationPriceTiersForSelection) || [];

    const storesByCatalogId: Record<number, StoreAttributes> = _.keyBy(stores, 'catalogId');

    const dispatch = useDispatch();

    const [products, setProducts] = useState<ProductInCatalog[]>([]);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadError, setIsLoadError] = useState<boolean>(false);
    const [priceTierPresenceInCatalog, setPriceTierPresenceInCatalog] =
        useState<PriceTierCatalogPresence>({});

    useEffect(() => {
        if (priceTier && needsRefresh) {
            setIsLoading(true);
            setIsLoadError(false);
            getProductsAssignedToPriceTier(priceTier.id, organizationCatalogId)
                .then((assignedProducts: ProductInCatalog[]) => {
                    setProducts(assignedProducts);
                    setNeedsRefresh(false);
                })
                .catch((reason) => {
                    console.error('Failed to load products', {reason});
                    dispatch(
                        showSnackbar({
                            onClose: () => {
                                dispatch(clearSnackbar());
                            },
                            type: SnackbarType.WARNING,
                            content: 'Failed to load products for reassignment',
                        })
                    );
                    setIsLoadError(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });

            getPriceTierPresenceInCatalog([priceTier.id]).then(
                (presenceData: PriceTierCatalogPresence) => {
                    setPriceTierPresenceInCatalog(presenceData);
                }
            );
        }
    }, [needsRefresh]);

    const handleSubmit = async (payload: ReassignPriceTierPayload) => {
        try {
            await reassignPriceTiers(payload, organizationCatalogId);
            dispatch(
                showSnackbar({
                    onClose: () => {
                        dispatch(clearSnackbar());
                    },
                    type: SnackbarType.SUCCESS,
                    content: 'Saved!',
                })
            );

            setNeedsRefresh(true);
            onSubmit();
        } catch (error) {
            console.error(error);
            openFeedbackModal(
                closeFeedbackModal,
                'Error Reassigning Products to a new Price Tier',
                {
                    type: ModalType.WARNING,
                    bodyText: 'There was an error reassigning products to a new price tier',
                    cancelOption: true,
                    onCancel: () => closeFeedbackModal(),
                }
            );
        }
    };

    const getPriceTierOptions = () => {
        return priceTierOptions.filter((p) => p.id !== priceTier!.id);
    };

    const getProducts = () => {
        if (products.length > MAX_NUMBER_OF_PRODUCTS_DISPLAY) {
            return products.slice(0, MAX_NUMBER_OF_PRODUCTS_DISPLAY);
        }

        return products;
    };

    const getPriceTierWithCatalogPresence = (
        // eslint-disable-next-line @typescript-eslint/no-shadow
        priceTier: PriceTierWithCatalogPresence
    ): PriceTierWithCatalogPresence => {
        if (_.isEmpty(priceTierPresenceInCatalog)) {
            return priceTier;
        }

        const catalogPresence: string[] = [];
        if (!_.isEmpty(priceTierPresenceInCatalog[priceTier.id])) {
            priceTierPresenceInCatalog[priceTier.id].forEach((catalogId) => {
                if (storesByCatalogId[catalogId]) {
                    const store = storesByCatalogId[catalogId];

                    catalogPresence.push(store.name);
                }
            });
        }

        return {
            ...priceTier,
            catalogPresence,
        };
    };

    if (open && priceTier) {
        return (
            <Modal onClose={onNotBackdropClickWrap(onClose)} open={open}>
                <div className="price-tier-reassign-container">
                    <PriceTierReassign
                        isLoading={isLoading}
                        isLoadError={isLoadError}
                        onClose={onClose}
                        onSubmit={handleSubmit}
                        priceTier={getPriceTierWithCatalogPresence(priceTier)}
                        priceTierOptions={getPriceTierOptions()}
                        products={getProducts()}
                        totalCount={products.length}
                    />
                </div>
            </Modal>
        );
    }

    return null;
};

export default PriceTierReassignModal;
